import { useT123ClubApi } from "../../lib/clubScope";
import { useEffect, useState, type ReactNode } from "react";

import { ApiError } from "../../lib/api";
import { formatMoney } from "../../lib/money";
import { type T123ClubSummary } from "../../lib/t123MembershipApi";

type Props = {
  token: string;
  reloadKey?: number;
  initialData?: T123ClubSummary | null;
  /** Dentro de la tarjeta «Membresía Torneos123 de tus socios» del panel Hoy. */
  embedded?: boolean;
  /** Lo que se ve mientras el cobro está apagado. */
  fallback?: ReactNode;
};

const BUCKETS: Array<{ key: keyof T123ClubSummary["counts"]; label: string }> = [
  { key: "al_corriente", label: "Al corriente" },
  { key: "en_gracia", label: "En gracia" },
  { key: "sin_pagar", label: "Sin pagar" },
];

/** Panel del club: «Membresía Torneos123» con conteo de socios por estado. */
export default function ClubT123MembershipTile({
  token,
  reloadKey = 0,
  initialData = null,
  embedded = false,
  fallback = null,
}: Props) {
  const t123Api = useT123ClubApi();
  const [data, setData] = useState<T123ClubSummary | null>(initialData);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<keyof T123ClubSummary["counts"] | null>(null);

  useEffect(() => {
    if (initialData) return;
    let alive = true;
    t123Api
      .clubSummary(token)
      .then((resp) => alive && setData(resp.data))
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : "No se pudo cargar la membresía."));
    return () => {
      alive = false;
    };
  }, [token, reloadKey, initialData]);

  if (error || !data || !data.billing_enabled) return <>{fallback}</>;

  let subtitle: string;
  if (data.membership_payer === "none") subtitle = "Sin cobro: todos tus socios pueden reservar.";
  else if (data.membership_payer === "club") subtitle = "La paga el club (próximamente).";
  else
    subtitle = `Cada socio paga ${formatMoney(data.price)} al mes. Gracia de ${data.grace_days} días; después ${
      data.after_grace === "suspended" ? "queda suspendido" : "no puede reservar"
    }.`;

  const rows = filter ? data.members.filter((m) => m.bucket === filter) : [];

  const body = (
    <>
      <p className="club-panel__muted">{subtitle}</p>
      <div className="t123-club-tile__counts">
        {BUCKETS.map((bucket) => (
          <button
            key={bucket.key}
            type="button"
            className={`t123-club-tile__count t123-club-tile__count--${bucket.key}`}
            aria-pressed={filter === bucket.key}
            onClick={() => setFilter(filter === bucket.key ? null : bucket.key)}
          >
            <strong>{data.counts[bucket.key]}</strong>
            <span>{bucket.label}</span>
          </button>
        ))}
      </div>
      {filter ? (
        rows.length === 0 ? (
          <p className="club-panel__muted">Nadie en este grupo.</p>
        ) : (
          <ul className="t123-club-tile__list">
            {rows.map((row) => (
              <li key={row.member_id}>
                <span>{row.name}</span>
                <small>{row.standing.label}</small>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </>
  );
  if (embedded) return <div className="t123-club-tile">{body}</div>;
  return (
    <section className="club-panel__card t123-club-tile" aria-label="Membresía Torneos123">
      <div className="club-panel__card-head">
        <h2>Membresía Torneos123</h2>
      </div>
      {body}
    </section>
  );
}
